import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, Building2, FileText, LayoutDashboard, MapPin, Users, Bot, Receipt, UserCheck, Cpu, Target, Store, ShoppingBag, MessageCircle, Briefcase, CreditCard, Stethoscope, Layers } from 'lucide-react';
import { Button } from './ui/button';

const navLinks = [
  { 
    label: 'Centre Commercial', 
    href: '#',
    hasDropdown: true,
    colorTheme: 'orange',
    submenu: [
      { label: 'Réseau Social Bâtiment', href: '/reseau', icon: MessageCircle, description: 'Partage tes chantiers' },
      { label: 'Trouver un Artisan', href: '/artisans', icon: Users, description: 'Annuaire des pros' },
      { label: 'Marketplace Matériaux', href: '/marketplace', icon: ShoppingBag, description: 'Achetez/Vendez' },
    ]
  },
  { 
    label: 'Espace Pro', 
    href: '#',
    hasDropdown: true,
    colorTheme: 'blue',
    submenu: [
      { label: 'Diagnostic Entrepreneur', href: '/diagnostic-entrepreneur', icon: Stethoscope, description: 'Évalue ton activité' },
      { label: 'Nos Solutions', href: '/nos-solutions', icon: Layers, description: 'Découvrez nos offres' },
      { label: 'CRM Bâtiment', href: '/fonctionnalites/crm-batiment', icon: Building2, description: 'Gère tes clients' },
      { label: 'Devis Intelligents', href: '/fonctionnalites/devis-intelligents', icon: FileText, description: 'Crée tes devis' },
      { label: 'Pipelines Commerciaux', href: '/fonctionnalites/pipelines', icon: LayoutDashboard, description: 'Suis tes affaires' },
      { label: 'Automatisation & IA', href: '/fonctionnalites/automatisation-ia', icon: Bot, description: 'Gagnez du temps' },
      { label: 'Super Assistants', href: '/super-assistant', icon: UserCheck, description: 'Assistance humaine' },
      { label: 'Tarifs', href: '/tarifs', icon: CreditCard, description: 'Nos offres' },
    ]
  },
];

// Composant Logo SVG sans fond - TRANSPARENT
function LogoBatiAssist({ className = "h-10", textSize = "text-xl" }) {
  return (
    <div className={`flex items-center gap-2`}>
      <svg viewBox="0 0 40 40" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 4L4 16V36H16V24H24V36H36V16L20 4Z" fill="#F97316"/>
        <path d="M20 4L4 16H12L20 10L28 16H36L20 4Z" fill="#EA580C"/>
      </svg>
      <span className={`font-bold ${textSize}`}>
        <span className="text-[#1E40AF]">Bati</span>
        <span className="text-[#F97316]">Assist</span>
      </span>
    </div>
  );
}

// Composant Logo Premium avec effet
function PremiumLogo({ className = "", size = "default" }) {
  const sizeConfig = {
    small: { iconClass: "h-8 sm:h-10", textSize: "text-lg sm:text-xl" },
    default: { iconClass: "h-10 sm:h-12 md:h-14 lg:h-16", textSize: "text-xl sm:text-2xl md:text-3xl lg:text-4xl" },
    large: { iconClass: "h-12 sm:h-14 md:h-16 lg:h-20", textSize: "text-2xl sm:text-3xl md:text-4xl lg:text-5xl" },
  };

  const config = sizeConfig[size];

  return (
    <motion.div 
      className={`relative group ${className}`}
      whileHover={{ scale: 1.03 }}
      transition={{ type: "spring", stiffness: 400 }}
    >
      <LogoBatiAssist className={config.iconClass} textSize={config.textSize} />
    </motion.div>
  );
}

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100' 
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        <nav className="flex items-center justify-between h-[72px] sm:h-[88px] lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <PremiumLogo size="default" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, index) => (
              link.hasDropdown ? (
                <div 
                  key={index}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(index)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    className={`flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 ${
                      link.colorTheme === 'orange' 
                        ? 'text-ba-secondary hover:bg-orange-50' 
                        : link.colorTheme === 'blue'
                        ? 'text-ba-primary hover:bg-blue-50'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === index ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {openDropdown === index && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50"
                      >
                        {link.submenu.map((sublink, subIndex) => {
                          const Icon = sublink.icon;
                          const isBlueTheme = link.colorTheme === 'blue';
                          const isOrangeTheme = link.colorTheme === 'orange';
                          return (
                            <Link
                              key={subIndex}
                              to={sublink.href}
                              className={`flex items-start gap-3 px-4 py-3 text-sm transition-colors ${
                                isBlueTheme 
                                  ? 'hover:text-ba-primary hover:bg-blue-50' 
                                  : isOrangeTheme
                                  ? 'hover:text-ba-secondary hover:bg-orange-50'
                                  : 'hover:text-ba-primary hover:bg-slate-50'
                              }`}
                            >
                              <Icon className={`w-5 h-5 mt-0.5 ${isBlueTheme ? 'text-ba-primary' : isOrangeTheme ? 'text-ba-secondary' : 'text-ba-secondary'}`} />
                              <div>
                                <div className="font-medium text-slate-700">{sublink.label}</div>
                                {sublink.description && (
                                  <div className="text-xs text-slate-500 mt-0.5">{sublink.description}</div>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={index}
                  to={link.href}
                  className={`px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 ${
                    link.highlight 
                      ? 'bg-ba-secondary/10 text-ba-secondary hover:bg-ba-secondary/20' 
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* CTA Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/reseau/connexion" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
              Se connecter
            </Link>
            <Link to="/reseau/inscription">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-6 py-2.5 shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5">
                Créer un compte
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6 text-slate-700" /> : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-slate-700">
                <rect x="3" y="3" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="10" y="3" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="17" y="3" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="3" y="10" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="10" y="10" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="17" y="10" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="3" y="17" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="10" y="17" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="17" y="17" width="5" height="5" rx="1" fill="currentColor"/>
              </svg>
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 top-[72px] bg-black/20 backdrop-blur-sm lg:hidden z-40"
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t border-slate-100 z-50 max-h-[80vh] overflow-y-auto"
              >
                <div className="py-4 px-4 space-y-1">
                  {navLinks.map((link, index) => (
                    link.hasDropdown ? (
                      <div key={index}>
                        <button
                          onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                          className={`flex items-center justify-between w-full px-4 py-3.5 rounded-xl font-medium ${
                            link.colorTheme === 'orange' 
                              ? 'text-ba-secondary bg-orange-50' 
                              : link.colorTheme === 'blue'
                              ? 'text-ba-primary bg-blue-50'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {link.label}
                          <ChevronDown className={`w-5 h-5 transition-transform ${openDropdown === index ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {openDropdown === index && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-4 space-y-1 mt-1"
                            >
                              {link.submenu.map((sublink, subIndex) => {
                                const Icon = sublink.icon;
                                const isBlueTheme = link.colorTheme === 'blue';
                                const isOrangeTheme = link.colorTheme === 'orange';
                                return (
                                  <Link
                                    key={subIndex}
                                    to={sublink.href}
                                    className={`flex items-start gap-3 px-4 py-3 rounded-xl text-sm ${
                                      isBlueTheme ? 'hover:bg-blue-50' : isOrangeTheme ? 'hover:bg-orange-50' : 'hover:bg-slate-50'
                                    }`}
                                  >
                                    <Icon className={`w-5 h-5 mt-0.5 ${isBlueTheme ? 'text-ba-primary' : isOrangeTheme ? 'text-ba-secondary' : 'text-ba-secondary'}`} />
                                    <div>
                                      <div className="font-medium text-slate-700">{sublink.label}</div>
                                      {sublink.description && (
                                        <div className="text-xs text-slate-500 mt-0.5">{sublink.description}</div>
                                      )}
                                    </div>
                                  </Link>
                                );
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        key={index}
                        to={link.href}
                        className={`block px-4 py-3.5 rounded-xl font-medium transition-colors ${
                          link.highlight 
                            ? 'text-ba-secondary bg-ba-secondary/5 hover:bg-ba-secondary/10' 
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {link.label}
                      </Link>
                    )
                  ))}
                  <div className="pt-4 px-2 space-y-2">
                    <Link to="/reseau/inscription" className="block">
                      <Button className="w-full bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full py-4 shadow-md">
                        Créer un compte
                      </Button>
                    </Link>
                    <Link to="/reseau/connexion" className="block">
                      <Button variant="outline" className="w-full border-slate-300 text-slate-700 rounded-full py-3">
                        Se connecter
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}

// Export le composant logo pour l'utiliser ailleurs
export { PremiumLogo, LogoBatiAssist };
